import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataGridPremium, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
// arch-ignore mui-menu -- okres w Popover (z-index 1300): lista „Rok" w PeriodPicker otwiera się z z-index 1500, a wspólny Popper ma globalnie 9999 i przykryłby ją
import { Popover } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { TFunction } from "i18next";
import moment from "moment";

import ebiOfficeService from "api/ebiOfficeService";
import DropdownMui from "components/DropdownMui";
import HollowButton from "components/HollowButton";
import PeriodPicker from "components/Registry/PeriodPicker";
import SearchEmptyImg from "components/SearchEmptyImg";
import SecondCheckbox from "components/SecondCheckbox";
import StatusBadge from "components/StatusBadge";
import { IDropdownObject } from "components/Dropdown/typings";
import { EEbiDeliveryStatus, IEbiRegisterItem, IEbiRegisterRequest } from "dto/Ebi/IEbiOffice";
import { IStatus } from "dto/IStatus";
import { ListType } from "dto/ListType";
import useGridRowSelection from "hooks/useGridRowSelection";
import ParcelPreviewModal from "modules/biuroPodawczeModule/ParcelPreviewModal";
import { plGridLocales } from "utils/muiGridLocales";
import { ENotificationType, notification } from "utils/notification";
import { IPrintRegisterColumn, printRegister } from "utils/printRegister";

import { FC, Props } from "./typings";
import styles from "./styles.module.scss";

// Rejestr EBI (Elektroniczne Biuro Interesanta, etap 6): przesyłki urzędu kanałem EBI z awizami, udostępnieniem
// petentowi, odbiorem (dowód E.1) albo domniemaniem doręczenia. Zakres jak w rejestrach biura podawczego — biuro
// podawcze, administrator, właściciel i zarząd widzą całą firmę, pozostali swoje przesyłki (BE).

const PAGE_SIZE = 50;
const ALL_STATES = "all";
const NOW = new Date();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => NOW.getFullYear() - i);

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fmtDate = (v?: string | null) => (v ? moment(v).format("DD.MM.YYYY") : "");
const fmtDateTime = (v?: string | null) => (v ? moment(v).format("DD.MM.YYYY HH:mm") : "");

/** Stan doręczenia jako status StatusBadge — kolory z palety statusów (getStatusColor). */
const statusOf = (row: IEbiRegisterItem, t: TFunction): IStatus => {
  const status = (id: number, name: string, color: number): IStatus => ({ Id: id, Name: name, Color: color, Type: ListType.None });

  if (row.WaitingForShare && row.Status !== EEbiDeliveryStatus.WaitingForKey)
    return status(-10, t("ebiOffice.register.status.waitingNewKey"), 6);

  switch (row.Status) {
    case EEbiDeliveryStatus.Prepared:
      return status(-11, t("ebiOffice.register.status.prepared"), 15);
    case EEbiDeliveryStatus.WaitingForKey:
      return status(-12, t("ebiOffice.register.status.waitingForKey"), 6);
    case EEbiDeliveryStatus.Available:
      return status(-13, t("ebiOffice.register.status.available"), 9);
    case EEbiDeliveryStatus.Received:
      return status(-14, t("ebiOffice.register.status.received"), 11);
    case EEbiDeliveryStatus.DeemedDelivered:
      return status(-15, t("ebiOffice.register.status.deemed"), 14);
    default:
      return status(-16, t("ebiOffice.register.status.cancelled"), 1);
  }
};

const deliveredLabel = (row: IEbiRegisterItem, t: TFunction) =>
  row.ReceivedDate
    ? fmtDateTime(row.ReceivedDate)
    : row.DeemedDeliveredDate
      ? t("ebiOffice.register.deemedOn", { date: fmtDateTime(row.DeemedDeliveredDate) })
      : "";

const CustomNoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.empty}>
      <SearchEmptyImg />
      <div>{t("ebiOffice.register.empty")}</div>
    </div>
  );
};

const EbiRegister: FC<Props> = ({ searchText, setSubtitle }) => {
  const { t } = useTranslation();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  const [year, setYear] = useState<number>(NOW.getFullYear());
  const [from, setFrom] = useState<string>(toISO(new Date(NOW.getFullYear(), 0, 1)));
  const [to, setTo] = useState<string>(toISO(NOW));
  const [preset, setPreset] = useState<string | null>("biezacyRok");
  const [periodAnchor, setPeriodAnchor] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<EEbiDeliveryStatus | null>(null);
  const [waitingForShare, setWaitingForShare] = useState(false);
  const [preview, setPreview] = useState<IEbiRegisterItem | null>(null);
  const [printing, setPrinting] = useState(false);

  // Strona obowiązuje tylko dla filtra, przy którym ją ustawiono (zmiana filtra = pierwsza strona).
  const filterKey = JSON.stringify([from, to, status, waitingForShare, searchText]);
  const [pageState, setPageState] = useState({ key: filterKey, page: 0 });
  const page = pageState.key === filterKey ? pageState.page : 0;

  const request: IEbiRegisterRequest = {
    From: from || null,
    To: to || null,
    Text: searchText || null,
    Status: status,
    WaitingForShare: waitingForShare,
    Skip: page * PAGE_SIZE,
    Take: PAGE_SIZE,
  };

  const { data, isLoading } = ebiOfficeService.useGetRegister(request);
  const result = data?.data;
  const total = result?.Total ?? 0;

  const rows = useMemo(() => (result?.Items ?? []).map((item) => ({ id: item.DeliveryId, ...item })), [result]);
  rowsRef.current = rows;

  const periodLabel = from || to ? `${fmtDate(from)} – ${fmtDate(to)}` : t("ebiOffice.register.allTime");

  useEffect(() => {
    setSubtitle(`${periodLabel}${result?.IsTeamView === false ? ` · ${t("ebiOffice.register.onlyMine")}` : ""}`);
  }, [periodLabel, result?.IsTeamView, setSubtitle, t]);

  // Wartości tekstowe: DropdownMui dopasowuje też Number(wartość), więc liczbowe 0 (Prepared) łapałoby pustą opcję.
  const statusOptions = useMemo<IDropdownObject[]>(
    () => [
      { name: t("ebiOffice.register.allStates"), value: ALL_STATES },
      { name: t("ebiOffice.register.status.waitingForKey"), value: String(EEbiDeliveryStatus.WaitingForKey) },
      { name: t("ebiOffice.register.status.available"), value: String(EEbiDeliveryStatus.Available) },
      { name: t("ebiOffice.register.status.received"), value: String(EEbiDeliveryStatus.Received) },
      { name: t("ebiOffice.register.status.deemed"), value: String(EEbiDeliveryStatus.DeemedDelivered) },
      { name: t("ebiOffice.register.status.prepared"), value: String(EEbiDeliveryStatus.Prepared) },
      { name: t("ebiOffice.register.status.cancelled"), value: String(EEbiDeliveryStatus.Cancelled) },
    ],
    [t],
  );

  const columns: GridColDef[] = [
    { field: "Signature", headerName: t("ebiOffice.register.number"), width: 120 },
    {
      field: "SendDate",
      headerName: t("ebiOffice.register.sendDate"),
      width: 110,
      valueFormatter: (v) => fmtDate(v as string),
    },
    {
      field: "RecipientName",
      headerName: t("ebiOffice.register.recipient"),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<IEbiRegisterItem>) => (
        <div className={styles.recipient}>
          <span className={styles.recipientName}>{params.row.RecipientName ?? ""}</span>
          <span className={styles.recipientEmail}>{params.row.Email}</span>
        </div>
      ),
    },
    { field: "Title", headerName: t("ebiOffice.register.title"), flex: 1, minWidth: 200 },
    {
      field: "Status",
      headerName: t("ebiOffice.register.state"),
      width: 210,
      renderCell: (params: GridRenderCellParams<IEbiRegisterItem>) => (
        <StatusBadge small showName status={statusOf(params.row, t)} />
      ),
    },
    {
      field: "NoticeDate",
      headerName: t("ebiOffice.register.notice"),
      width: 130,
      valueFormatter: (v) => fmtDateTime(v as string),
    },
    {
      field: "SecondNoticeDate",
      headerName: t("ebiOffice.register.secondNotice"),
      width: 130,
      valueFormatter: (v) => fmtDateTime(v as string),
    },
    {
      field: "ReceivedDate",
      headerName: t("ebiOffice.register.delivered"),
      width: 170,
      valueGetter: (_v, row: IEbiRegisterItem) => deliveredLabel(row, t),
    },
    {
      field: "DeemedDeliveryDue",
      headerName: t("ebiOffice.register.due"),
      width: 130,
      valueFormatter: (v) => fmtDateTime(v as string),
    },
    { field: "Author", headerName: t("ebiOffice.register.author"), width: 150 },
    { field: "CaseSignature", headerName: t("ebiOffice.register.case"), width: 140 },
    {
      field: "LastError",
      headerName: t("ebiOffice.register.remarks"),
      width: 220,
      renderCell: (params: GridRenderCellParams<IEbiRegisterItem>) =>
        params.row.LastError ? <span className={styles.error}>{params.row.LastError}</span> : null,
    },
  ];

  const doPrint = async () => {
    setPrinting(true);

    try {
      const { data: all } = await ebiOfficeService.getRegister({ ...request, Skip: 0, Take: 0 });

      if (all.Total > all.Items.length) {
        notification({
          type: ENotificationType.INFO,
          title: t("ebiOffice.register.print"),
          text: t("ebiOffice.register.printTruncated", { printed: all.Items.length, total: all.Total }),
        });
      }

      const printColumns: IPrintRegisterColumn<IEbiRegisterItem>[] = [
        { header: t("ebiOffice.register.lp"), value: (_, i) => i + 1, width: "4%", align: "center" },
        { header: t("ebiOffice.register.number"), value: (r) => r.Signature, width: "9%" },
        { header: t("ebiOffice.register.sendDate"), value: (r) => fmtDate(r.SendDate), width: "8%", align: "center" },
        { header: t("ebiOffice.register.recipient"), value: (r) => [r.RecipientName, r.Email].filter(Boolean).join(", "), width: "20%" },
        { header: t("ebiOffice.register.title"), value: (r) => r.Title, width: "19%" },
        { header: t("ebiOffice.register.state"), value: (r) => statusOf(r, t).Name, width: "11%" },
        { header: t("ebiOffice.register.notice"), value: (r) => fmtDate(r.NoticeDate), width: "8%", align: "center" },
        { header: t("ebiOffice.register.delivered"), value: (r) => deliveredLabel(r, t), width: "12%" },
        { header: t("ebiOffice.register.author"), value: (r) => r.Author, width: "9%" },
      ];

      const ok = printRegister<IEbiRegisterItem>({
        title: t("ebiOffice.register.printTitle"),
        subtitle: periodLabel,
        columns: printColumns,
        rows: all.Items,
      });

      if (!ok) {
        notification({
          type: ENotificationType.ERROR,
          title: t("ebiOffice.register.print"),
          text: t("ebiOffice.register.printBlocked"),
        });
      }
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: t("ebiOffice.register.print"),
        text: t("ebiOffice.register.printFailed"),
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <div
            className={styles.periodBtn}
            role="button"
            tabIndex={0}
            onClick={(e) => setPeriodAnchor(periodAnchor ? null : e.currentTarget)}
            onKeyDown={(e) => e.key === "Enter" && setPeriodAnchor(e.currentTarget)}
          >
            {periodLabel}
            <FontAwesomeIcon icon={periodAnchor ? faChevronUp : faChevronDown} />
          </div>

          <div className={styles.statusFilter}>
            <DropdownMui
              list={statusOptions}
              defaultValue={status === null ? ALL_STATES : String(status)}
              label={t("ebiOffice.register.state")}
              size="small"
              onChange={(option?: IDropdownObject) =>
                setStatus(!option || option.value === ALL_STATES ? null : (Number(option.value) as EEbiDeliveryStatus))
              }
            />
          </div>

          <SecondCheckbox
            value={waitingForShare}
            onChange={setWaitingForShare}
            text={t("ebiOffice.register.onlyWaiting")}
          />
        </div>

        <div className={styles.toolbarRight}>
          <div className={styles.count}>{t("ebiOffice.register.count", { count: total })}</div>
          <HollowButton text={t("ebiOffice.register.print")} onClick={doPrint} disabled={printing} />
        </div>
      </div>

      <div className={styles.list}>
        <DataGridPremium
          localeText={plGridLocales}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
          loading={isLoading}
          columnHeaderHeight={30}
          rowHeight={44}
          columns={columns}
          rows={rows}
          rowCount={total}
          paginationMode="server"
          pagination
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          pageSizeOptions={[PAGE_SIZE]}
          onPaginationModelChange={(m) => setPageState({ key: filterKey, page: m.page })}
          disableColumnFilter
          disablePivoting
          disableAggregation
          disableRowGrouping
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          onRowDoubleClick={(p) => setPreview(p.row as IEbiRegisterItem)}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
          }}
        />
      </div>

      {preview && (
        <ParcelPreviewModal parcelId={preview.OutgoingParcelId} title={preview.Title} onClose={() => setPreview(null)} />
      )}

      <Popover
        open={!!periodAnchor}
        anchorEl={periodAnchor}
        onClose={() => setPeriodAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div className={styles.periodDropdown}>
          <PeriodPicker
            year={year}
            from={from}
            to={to}
            preset={preset}
            yearOptions={YEAR_OPTIONS}
            setYear={setYear}
            setFrom={setFrom}
            setTo={setTo}
            setPreset={setPreset}
          />
          <HollowButton
            text={t("ebiOffice.register.allTime")}
            onClick={() => {
              setFrom("");
              setTo("");
              setPreset(null);
            }}
          />
        </div>
      </Popover>
    </div>
  );
};

export default EbiRegister;
